import type { AppState, Expense, PaymentMethod, RecurringExpense, Settings } from '../types';

const STORAGE_KEY = 'quickspend-state-v1';

export const defaultSettings: Settings = {
  currency: 'INR',
  monthlyBudget: 25000,
  shakeEnabled: false,
  shakeSensitivity: 6,
  defaultPaymentMethod: 'UPI',
  theme: 'light',
  syncEnabled: true,
  sheetsConnected: false,
  lastSyncAt: null,
  alertThreshold: 80,
  notificationsEnabled: false,
};

export const defaultState: AppState = {
  version: 1,
  expenses: [],
  recurringExpenses: [],
  settings: defaultSettings,
  syncQueue: [],
};

function isPaymentMethod(value: unknown): value is PaymentMethod {
  return typeof value === 'string' && ['Cash', 'UPI', 'Debit card', 'Credit card', 'Bank transfer', 'Other'].includes(value);
}

function normalizeExpense(value: unknown): Expense | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<Expense>;
  if (typeof item.id !== 'string' || typeof item.name !== 'string' || typeof item.amount !== 'number') return null;
  const occurredDate = typeof item.occurredAt === 'string' ? new Date(item.occurredAt) : new Date(NaN);
  const occurredAt = Number.isNaN(occurredDate.getTime()) ? new Date().toISOString() : occurredDate.toISOString();
  const createdDate = typeof item.createdAt === 'string' ? new Date(item.createdAt) : new Date(NaN);
  const createdAt = Number.isNaN(createdDate.getTime()) ? occurredAt : createdDate.toISOString();
  const updatedDate = typeof item.updatedAt === 'string' ? new Date(item.updatedAt) : new Date(NaN);
  const updatedAt = Number.isNaN(updatedDate.getTime()) ? createdAt : updatedDate.toISOString();
  return {
    id: item.id,
    name: item.name,
    amount: Math.max(0, item.amount),
    kind: item.kind === 'inflow' ? 'inflow' : 'outflow',
    category: typeof item.category === 'string' ? (item.category as Expense['category']) : 'Other',
    occurredAt,
    paymentMethod: isPaymentMethod(item.paymentMethod) ? item.paymentMethod : 'Other',
    notes: typeof item.notes === 'string' ? item.notes : '',
    createdAt,
    updatedAt,
    syncStatus: item.syncStatus === 'synced' || item.syncStatus === 'pending' || item.syncStatus === 'error' ? item.syncStatus : 'local',
    recurringId: typeof item.recurringId === 'string' ? item.recurringId : undefined,
  };
}

function normalizeRecurring(value: unknown): RecurringExpense | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<RecurringExpense>;
  const dueDate = typeof item.nextDueAt === 'string' ? new Date(item.nextDueAt) : new Date(NaN);
  if (typeof item.id !== 'string' || typeof item.name !== 'string' || typeof item.amount !== 'number' || Number.isNaN(dueDate.getTime())) return null;
  return {
    id: item.id,
    name: item.name,
    amount: Math.max(0, item.amount),
    category: typeof item.category === 'string' ? (item.category as RecurringExpense['category']) : 'Other',
    paymentMethod: isPaymentMethod(item.paymentMethod) ? item.paymentMethod : 'Other',
    notes: typeof item.notes === 'string' ? item.notes : '',
    cadence: item.cadence === 'monthly' ? 'monthly' : 'weekly',
    monthlyDay: typeof item.monthlyDay === 'number' ? Math.min(31, Math.max(1, item.monthlyDay)) : dueDate.getDate(),
    reminderEnabled: item.reminderEnabled !== false,
    reminderHoursBefore: typeof item.reminderHoursBefore === 'number' ? Math.min(168, Math.max(1, item.reminderHoursBefore)) : 24,
    nextDueAt: dueDate.toISOString(),
    active: item.active !== false,
    createdAt: typeof item.createdAt === 'string' && !Number.isNaN(new Date(item.createdAt).getTime()) ? new Date(item.createdAt).toISOString() : new Date().toISOString(),
  };
}

export function loadState(): AppState {
  if (typeof window === 'undefined') return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as Partial<AppState>;
    const expenses = Array.isArray(parsed.expenses) ? parsed.expenses.map(normalizeExpense).filter(Boolean) as Expense[] : [];
    const recurringExpenses = Array.isArray(parsed.recurringExpenses) ? parsed.recurringExpenses.map(normalizeRecurring).filter(Boolean) as RecurringExpense[] : [];
    const incomingSettings = parsed.settings && typeof parsed.settings === 'object' ? parsed.settings as Partial<Settings> : {};
    const storedQueue = Array.isArray(parsed.syncQueue) ? parsed.syncQueue.filter((id): id is string => typeof id === 'string') : [];
    const queueIds = new Set([...storedQueue, ...expenses.filter((expense) => expense.syncStatus === 'pending').map((expense) => expense.id)]);
    const validExpenseIds = new Set(expenses.map((expense) => expense.id));
    const liveSheetsConnection = false;
    const syncQueue = liveSheetsConnection ? [...queueIds].filter((id) => validExpenseIds.has(id)) : [];
    const reconciledExpenses = expenses.map((expense) => syncQueue.includes(expense.id) ? { ...expense, syncStatus: 'pending' as const } : expense.syncStatus === 'pending' ? { ...expense, syncStatus: 'local' as const } : expense);
    const lastSyncDate = typeof incomingSettings.lastSyncAt === 'string' ? new Date(incomingSettings.lastSyncAt) : new Date(NaN);
    return {
      version: 1,
      expenses: reconciledExpenses,
      recurringExpenses,
      syncQueue,
      settings: {
        ...defaultSettings,
        ...incomingSettings,
        sheetsConnected: liveSheetsConnection,
        monthlyBudget: typeof incomingSettings.monthlyBudget === 'number' ? Math.max(0, incomingSettings.monthlyBudget) : defaultSettings.monthlyBudget,
        shakeSensitivity: typeof incomingSettings.shakeSensitivity === 'number' ? Math.min(10, Math.max(1, incomingSettings.shakeSensitivity)) : defaultSettings.shakeSensitivity,
        alertThreshold: typeof incomingSettings.alertThreshold === 'number' ? Math.min(100, Math.max(50, incomingSettings.alertThreshold)) : defaultSettings.alertThreshold,
        notificationsEnabled: incomingSettings.notificationsEnabled === true,
        lastSyncAt: Number.isNaN(lastSyncDate.getTime()) ? null : lastSyncDate.toISOString(),
      },
    };
  } catch {
    return defaultState;
  }
}

function advanceDueDate(value: string, cadence: RecurringExpense['cadence'], monthlyDay?: number) {
  const date = new Date(value);
  if (cadence === 'daily') date.setDate(date.getDate() + 1);
  else if (cadence === 'monthly') {
    const day = monthlyDay || date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(day, lastDay));
  } else date.setDate(date.getDate() + 7);
  return date.toISOString();
}

export function materializeDueRecurring(state: AppState, now = new Date()): AppState {
  const expenses = [...state.expenses];
  const recurringExpenses = state.recurringExpenses.map((template) => {
    if (!template.active) return template;
    let nextDueAt = template.nextDueAt;
    let guard = 0;
    while (new Date(nextDueAt).getTime() <= now.getTime() && guard < 1000) {
      if (expenses.some((expense) => expense.recurringId === template.id && expense.occurredAt === nextDueAt)) {
        nextDueAt = advanceDueDate(nextDueAt, template.cadence, template.monthlyDay);
        guard += 1;
        continue;
      }
      const createdAt = new Date().toISOString();
      expenses.unshift({
        id: createId(),
        name: template.name,
        amount: template.amount,
        kind: 'outflow',
        category: template.category,
        occurredAt: nextDueAt,
        paymentMethod: template.paymentMethod,
        notes: template.notes ? `${template.notes} · recurring` : 'Recurring expense',
        createdAt,
        updatedAt: createdAt,
        syncStatus: state.settings.syncEnabled && state.settings.sheetsConnected ? 'pending' : 'local',
        recurringId: template.id,
      });
      nextDueAt = advanceDueDate(nextDueAt, template.cadence, template.monthlyDay);
      guard += 1;
    }
    return { ...template, nextDueAt };
  });
  const generated = expenses.slice(0, expenses.length - state.expenses.length);
  const generatedQueue = generated.filter((expense) => expense.syncStatus === 'pending').map((expense) => expense.id);
  return { ...state, expenses, recurringExpenses, syncQueue: state.settings.sheetsConnected ? [...new Set([...state.syncQueue, ...generatedQueue])] : [] };
}

export function saveState(state: AppState) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function clearStoredState() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function createId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
